import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Check, GraduationCap, Clock, TrendingUp, Award } from "lucide-react";

const FEATURES = [
  "الفوتوشوب من الأساسيات إلى الاحتراف",
  "تصميم المنتجات باستخدام الذكاء الاصطناعي",
  "هندسة البرومبت (Prompt Engineering)",
  "تصميم السوشيال ميديا والإعلانات",
  "بناء بورتفوليو احترافي",
];

const CARDS = [
  { icon: GraduationCap, value: "8", label: "محاضرات تدريبية" },
  { icon: Clock, value: "2h", label: "ساعتان لكل محاضرة" },
  { icon: TrendingUp, value: "10", label: "أيام متابعة وإرشاد بعد الدورة" },
  { icon: Award, value: "★", label: "شهادة مشاركة بالدورة" },
];

const WA_ENROLL =
  "https://wa.me/9647825579755?text=" +
  encodeURIComponent("مرحبا باسط، أريد التسجيل في دورة الفوتوشوب والذكاء الاصطناعي.");

export default function Course() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="course"
      data-testid="course-section"
      ref={ref}
      className="relative py-32 md:py-48 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/33714950/pexels-photo-33714950.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#171717] via-[#171717]/85 to-[#171717]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[#FD1843]/8 blur-[160px]" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="flex items-center mb-14"
        >
          <span className="h-rule" />
          <span className="overline">04 — The Course</span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start" dir="rtl">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.1 }}
            className="lg:col-span-7 space-y-10 font-arabic"
          >
            <h2 className="font-arabic text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.25]">
              دورة الفوتوشوب <br />
              والذكاء <span className="text-[#FD1843] glow-text">الاصطناعي</span> للمنتجات
            </h2>
            <p className="text-white/75 text-lg md:text-xl font-medium max-w-2xl leading-[1.95]">
              دورة عملية مخصصة للمبتدئين والمصممين الراغبين بتطوير مهاراتهم في
              الفوتوشوب والذكاء الاصطناعي وإنشاء تصاميم احترافية للسوشيال ميديا
              والإعلانات وبناء بورتفوليو قوي للعمل مع العملاء.
            </p>

            <ul className="space-y-4 max-w-xl">
              {FEATURES.map((f, i) => (
                <motion.li
                  key={f}
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.7, delay: 0.3 + i * 0.08 }}
                  className="flex items-start gap-4 text-white/90 text-base md:text-lg border-b border-white/5 pb-4"
                >
                  <span className="mt-1 w-6 h-6 shrink-0 rounded-full grid place-items-center bg-[#FD1843]/12 text-[#FD1843] border border-[#FD1843]/30">
                    <Check size={12} />
                  </span>
                  <span className="font-medium">{f}</span>
                </motion.li>
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-4 pt-4" dir="ltr">
              <a
                data-testid="course-cta-join"
                href={WA_ENROLL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  const w = window.open(WA_ENROLL, "_blank", "noopener,noreferrer");
                  if (!w) { try { window.top.location.href = WA_ENROLL; } catch { window.location.href = WA_ENROLL; } }
                }}
                className="btn-cyan font-arabic font-bold"
                data-magnetic
              >
                التسجيل عبر واتساب <ArrowUpRight size={16} />
              </a>
              <div
                dir="rtl"
                className="font-arabic text-white/70 text-sm md:text-[15px] font-medium flex items-center gap-2"
                data-testid="course-private-note"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#FD1843]" style={{ boxShadow: "0 0 10px #FD1843" }} />
                متوفر دورات خصوصية
              </div>
            </div>
          </motion.div>

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="lg:col-span-5"
            dir="rtl"
          >
            <div className="relative bg-[#1c1c1c]/70 backdrop-blur-xl border border-white/10 rounded-sm p-8 md:p-10 space-y-7 font-arabic">
              <div className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-[#FD1843] to-transparent" />

              <div className="flex items-baseline justify-between">
                <div className="font-display text-[#FD1843] font-semibold text-lg" dir="ltr">
                  2026
                </div>
                <div className="text-[11px] tracking-tight text-white/50 font-medium">
                  اونلاين / حضوري
                </div>
              </div>

              <div>
                <div className="font-display text-4xl md:text-5xl font-semibold tracking-[-0.03em] text-white" dir="rtl">
                  8 محاضرات
                </div>
                <div className="text-white/60 mt-1 font-medium">
                  دورة الفوتوشوب والذكاء الاصطناعي للمنتجات
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                {CARDS.map((c) => {
                  const Icon = c.icon;
                  return (
                    <div
                      key={c.label}
                      className="border border-white/8 p-4 space-y-2"
                      data-testid={`course-card-${c.label}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-display text-2xl text-[#FD1843]">
                          {c.value}
                        </span>
                        <Icon size={16} className="text-white/40" />
                      </div>
                      <div className="text-[12px] text-white/70 leading-snug font-medium">
                        {c.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 border-t border-white/5 flex items-center justify-between">
                <div className="text-[12px] text-white/50 font-medium">
                  التسجيل مفتوح الآن
                </div>
                <div className="text-[#FD1843] font-bold text-sm">
                  المقاعد محدودة
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
